import { Trans, t } from '@lingui/macro';
import { useState } from "react";
import { useAppUIContext } from "../../../context/AppUIContext";
import { Room } from "../../../models/Room";
import { Soribot } from "../../../models/Soribot";
import { Fire } from "../../../services/FireClient";
import { My } from "../../../services/My";
import LimitedFeature from "../../components/LimitedFeature";
import ProfileImg from "../../components/ProfileImg";
import { Link } from 'react-router-dom';

import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar";
import { Progress } from "../../../components/ui/progress";
import { useToast } from "../../../hooks/use-toast";
import { Input } from "../../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Badge } from "../../../components/ui/badge";

export const BotSoriPart: React.FC<{ room: Room }> = ({ room }) => {
  const { soribots } = useAppUIContext();
  const { toast } = useToast();
  const [inProgress, setInProgress] = useState(false);

  // Create Bot Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newBotName, setNewBotName] = useState("");

  const soribotsInRoom = soribots?.filter(b => b.roomIds.includes(room.id)) ?? [];

  return (
    <>
      <Card className="w-full mt-4">
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg font-medium"><Trans>Soribots</Trans></CardTitle>
            <Badge variant="secondary" className="bg-yellow-500/15 text-yellow-700 hover:bg-yellow-500/25">PREMIUM</Badge>
          </div>
          <LimitedFeature
            workspaceId={room.workspaceId}
            tier={'PREMIUM'}
            content={() => (
              <Button size="sm" onClick={() => addBot()}>
                <Trans>Add</Trans>
              </Button>
            )}
          />
        </CardHeader>
        <CardContent className="grid gap-2">
          {inProgress && <div className="py-2"><Progress value={undefined} className="h-1" /></div>}

          {!inProgress && soribotsInRoom.length === 0 && (
            <p className="text-sm text-muted-foreground italic py-2">
              <Trans>Click add to create a Soribot.</Trans>
            </p>
          )}

          {soribotsInRoom.map(b => getBotItem(b))}
        </CardContent>
      </Card>

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Trans>Create a Soribot in {Room.getRoomNameWithSharp(room, My.user.id)}</Trans>
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder={t`Name`}
              value={newBotName}
              onChange={(e) => setNewBotName(e.target.value)}
              disabled={inProgress}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateBot();
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} disabled={inProgress}>
              <Trans>Cancel</Trans>
            </Button>
            <Button onClick={handleCreateBot} disabled={inProgress}>
              <Trans>Create</Trans>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  function addBot(): void {
    if (!room.workspaceId) {
      toast({ title: t`Error`, description: t`This channel is not in a workspace.`, duration: 3000 });
      return;
    }
    setNewBotName("");
    setShowCreateModal(true);
  }

  function handleCreateBot() {
    setInProgress(true);
    Fire.bot.createSoribot({
      name: newBotName || t`Unnamed`,
      workspaceId: room.workspaceId!,
      roomId: room.id,
      owner: My.user,
      embeddingModel: 'text-embedding-ada-002'
    })
      .then(bot => {
        toast({ title: t`Success`, description: t`Soribot ${bot.name} created.`, duration: 2000 });
        setShowCreateModal(false);
      })
      .catch(error => {
        toast({
          title: t`Failed to create a Soribot`,
          description: error.message,
        });
      })
      .finally(() => setInProgress(false));
  }

  function getBotItem(b: Soribot) {
    return (
      <Link to={`/soribot/details/${b.id}`} key={b.id} className="block group">
        <div className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted transition-colors">
          <Avatar className="h-10 w-10">
            <AvatarImage src={b.imageUrl ?? `/assets/img/ai-soribot.png`} alt={b.name} />
            <AvatarFallback>{b.name[0]}</AvatarFallback>
            {/* Using ProfileImg component logic roughly here or assuming AvatarImage handles it */}
            <div className="absolute inset-0 flex items-center justify-center w-full h-full bg-transparent">
              <ProfileImg url={b.imageUrl ?? `/assets/img/ai-soribot.png`} className="w-full h-full object-cover rounded-full" />
            </div>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[color:rgb(var(--text-body))] group-hover:text-blue-600 truncate">
              {b.name}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {b.description ? b.description : "No description"}
            </p>
          </div>
        </div>
      </Link>
    );
  }
}
export default BotSoriPart;